import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useOrg } from "@/lib/org-context";

export function OrgAdminOverviewPage() {
  const { orgId, organization } = useOrg();
  const [usage, setUsage] = useState<{
    conferenceCount: number;
    streamCount: number;
    maxConferences: number;
    maxStreams: number;
  } | null>(null);

  useEffect(() => {
    void api<{
      plan: { maxConferences: number; maxStreams: number };
      usage: { conferences: number; streams: number };
    }>(`/api/accounts/${orgId}/usage`)
      .then((u) => {
        setUsage({
          conferenceCount: u.usage.conferences,
          streamCount: u.usage.streams,
          maxConferences: u.plan.maxConferences,
          maxStreams: u.plan.maxStreams,
        });
      })
      .catch(() => setUsage(null));
  }, [orgId]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-rs-ink">Workspace Overview</h2>
      <p className="text-sm text-rs-ink-muted">
        Usage and configuration for {organization?.name}.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          {
            label: "Conferences",
            value: usage
              ? `${usage.conferenceCount}/${usage.maxConferences}`
              : "—",
          },
          {
            label: "Sync Streams",
            value: usage ? `${usage.streamCount}/${usage.maxStreams}` : "—",
          },
          { label: "Your Role", value: organization?.role ?? "—" },
        ].map((s) => (
          <Panel key={s.label} className="p-4">
            <p className="text-sm text-rs-ink-muted">{s.label}</p>
            <p className="mt-1 text-xl font-semibold capitalize text-rs-ink">
              {s.value}
            </p>
          </Panel>
        ))}
      </div>
    </div>
  );
}

type AuditEvent = {
  id: string;
  action: string;
  resourceType?: string | null;
  resourceId?: string | null;
  createdAt: number;
  actorEmail?: string | null;
  actorUserId?: string | null;
};

type AuditSortKey =
  "createdAt" | "actor" | "action" | "resourceType" | "resourceId";

type AuditSortDir = "asc" | "desc";

function actorLabel(event: AuditEvent): string {
  return event.actorEmail?.trim() || event.actorUserId?.trim() || "system";
}

function compareAuditEvents(
  a: AuditEvent,
  b: AuditEvent,
  key: AuditSortKey,
  dir: AuditSortDir,
): number {
  const mul = dir === "asc" ? 1 : -1;
  let left: string | number;
  let right: string | number;
  switch (key) {
    case "createdAt":
      left = a.createdAt;
      right = b.createdAt;
      break;
    case "actor":
      left = actorLabel(a).toLowerCase();
      right = actorLabel(b).toLowerCase();
      break;
    case "action":
      left = a.action.toLowerCase();
      right = b.action.toLowerCase();
      break;
    case "resourceType":
      left = (a.resourceType ?? "").toLowerCase();
      right = (b.resourceType ?? "").toLowerCase();
      break;
    case "resourceId":
      left = (a.resourceId ?? "").toLowerCase();
      right = (b.resourceId ?? "").toLowerCase();
      break;
  }
  if (left < right) return -1 * mul;
  if (left > right) return 1 * mul;
  return a.id.localeCompare(b.id) * mul;
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  className,
}: {
  label: string;
  sortKey: AuditSortKey;
  activeKey: AuditSortKey;
  direction: AuditSortDir;
  onSort: (key: AuditSortKey) => void;
  className?: string;
}) {
  const active = activeKey === sortKey;
  const Icon = !active
    ? ArrowUpDown
    : direction === "asc"
      ? ArrowUp
      : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={
        active ? (direction === "asc" ? "ascending" : "descending") : "none"
      }
      className={cn("px-4 py-3 font-medium", className)}
    >
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-rs-sm text-left uppercase tracking-wide hover:text-rs-ink",
          active ? "text-rs-ink" : "text-rs-ink-muted",
        )}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </button>
    </th>
  );
}

export function OrgAdminAuditPage() {
  const { orgId } = useOrg();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<AuditSortKey>("createdAt");
  const [sortDir, setSortDir] = useState<AuditSortDir>("desc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api<{ events: AuditEvent[] }>(`/api/accounts/${orgId}/audit`)
      .then((r) => {
        if (!cancelled) setEvents(r.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const sorted = useMemo(
    () =>
      [...events].sort((a, b) => compareAuditEvents(a, b, sortKey, sortDir)),
    [events, sortKey, sortDir],
  );

  function onSort(key: AuditSortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "createdAt" ? "desc" : "asc");
  }

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-rs-ink">Audit Log</h2>
        <p className="text-sm text-rs-ink-muted">
          Recent workspace activity. Click a column header to sort.
        </p>
      </div>
      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-rs-border bg-rs-muted text-xs uppercase tracking-wide text-rs-ink-muted">
              <tr>
                <SortableTh
                  label="When"
                  sortKey="createdAt"
                  activeKey={sortKey}
                  direction={sortDir}
                  onSort={onSort}
                  className="whitespace-nowrap"
                />
                <SortableTh
                  label="Actor"
                  sortKey="actor"
                  activeKey={sortKey}
                  direction={sortDir}
                  onSort={onSort}
                />
                <SortableTh
                  label="Action"
                  sortKey="action"
                  activeKey={sortKey}
                  direction={sortDir}
                  onSort={onSort}
                />
                <SortableTh
                  label="Resource"
                  sortKey="resourceType"
                  activeKey={sortKey}
                  direction={sortDir}
                  onSort={onSort}
                />
                <SortableTh
                  label="Resource Id"
                  sortKey="resourceId"
                  activeKey={sortKey}
                  direction={sortDir}
                  onSort={onSort}
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((e, i) => (
                <tr
                  key={e.id}
                  className={i % 2 === 1 ? "bg-rs-muted/50" : "bg-rs-elevated"}
                >
                  <td className="whitespace-nowrap px-4 py-3 text-rs-ink-muted">
                    {new Date(e.createdAt * 1000).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-rs-ink">{actorLabel(e)}</td>
                  <td className="px-4 py-3 font-medium text-rs-ink">
                    {e.action}
                  </td>
                  <td className="px-4 py-3 text-rs-ink-muted">
                    {e.resourceType || "—"}
                  </td>
                  <td className="max-w-[12rem] truncate px-4 py-3 font-mono text-xs text-rs-ink-muted">
                    {e.resourceId || "—"}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-rs-ink-muted"
                  >
                    No audit events.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

type AccountUsageResponse = {
  plan: {
    planCode: string;
    maxConferences: number;
    maxStreams: number;
    maxRunsPerDay: number;
  };
  usage: {
    conferences: number;
    streams: number;
    runsToday: number;
  };
};

function formatPlanLabel(planCode: string): string {
  if (planCode === "free") return "Free Trial";
  if (planCode === "paid") return "Paid";
  return planCode
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function usageFraction(used: number, max: number): number {
  if (max <= 0) return used > 0 ? 1 : 0;
  return Math.min(1, Math.max(0, used / max));
}

function UsageMeter({
  label,
  used,
  max,
  helper,
}: {
  label: string;
  used: number;
  max: number;
  helper?: string;
}) {
  const fraction = usageFraction(used, max);
  const nearLimit = fraction >= 0.8;
  const atLimit = fraction >= 1;

  return (
    <Panel className="p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm text-rs-ink-muted">{label}</p>
        <p className="text-sm tabular-nums text-rs-ink">
          <span className="text-xl font-semibold">{used.toLocaleString()}</span>
          <span className="text-rs-ink-muted"> / {max.toLocaleString()}</span>
        </p>
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-rs-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={used}
      >
        <div
          className={
            atLimit
              ? "h-full rounded-full bg-rs-danger"
              : nearLimit
                ? "h-full rounded-full bg-rs-warning"
                : "h-full rounded-full bg-rs-accent"
          }
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
      {helper ? (
        <p className="mt-2 text-xs text-rs-ink-muted">{helper}</p>
      ) : null}
    </Panel>
  );
}

export function OrgAdminUsagePage() {
  const { orgId, organization } = useOrg();
  const [data, setData] = useState<AccountUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api<AccountUsageResponse>(`/api/accounts/${orgId}/usage`)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError("Could not load usage for this workspace.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-rs-ink">Usage</h2>
        <p className="text-sm text-rs-ink-muted">
          Plan limits and current consumption for {organization?.name}.
        </p>
      </div>

      {error || !data ? (
        <Panel className="p-5">
          <p className="text-sm text-rs-ink-muted">
            {error ?? "Usage data unavailable."}
          </p>
        </Panel>
      ) : (
        <>
          <Panel className="p-5">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-rs-ink-muted">Plan</dt>
                <dd className="font-medium text-rs-ink">
                  {formatPlanLabel(data.plan.planCode)}
                </dd>
              </div>
            </dl>
          </Panel>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <UsageMeter
              label="Conferences"
              used={data.usage.conferences}
              max={data.plan.maxConferences}
            />
            <UsageMeter
              label="Sync Streams"
              used={data.usage.streams}
              max={data.plan.maxStreams}
            />
            <UsageMeter
              label="Runs Today"
              used={data.usage.runsToday}
              max={data.plan.maxRunsPerDay}
              helper="Resets daily at midnight UTC."
            />
          </div>
        </>
      )}
    </div>
  );
}

export function OrgAdminSettingsPage() {
  const { orgId, organization, role } = useOrg();
  const [confirmOffboard, setConfirmOffboard] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function offboard() {
    await api(`/api/accounts/${orgId}/offboard`, {
      method: "POST",
      body: "{}",
    });
    setMessage(`Offboarding started for ${organization?.name}.`);
    setConfirmOffboard(false);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-rs-ink">Workspace Settings</h2>
      <Panel className="p-5">
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-rs-ink-muted">Name</dt>
            <dd className="text-rs-ink">{organization?.name}</dd>
          </div>
        </dl>
      </Panel>
      {role === "owner" ? (
        <Panel className="space-y-3 p-5">
          <h3 className="font-semibold text-rs-danger">Offboard Workspace</h3>
          <p className="text-sm text-rs-ink-muted">
            This affects {organization?.name} only. Data retention follows
            platform policy.
          </p>
          <Button variant="danger" onClick={() => setConfirmOffboard(true)}>
            Offboard {organization?.name}
          </Button>
        </Panel>
      ) : (
        <p className="text-sm text-rs-ink-muted">
          Only the owner can offboard this workspace.
        </p>
      )}
      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}
      <ConfirmDialog
        open={confirmOffboard}
        onOpenChange={setConfirmOffboard}
        title="Offboard Workspace"
        description="This schedules workspace offboarding and related cleanup."
        impact={`Exact target: ${organization?.name} (${orgId})`}
        confirmLabel={`Offboard ${organization?.name}`}
        dangerous
        onConfirm={offboard}
      />
    </div>
  );
}

export function OrgAdminSimplePage({
  title,
  children,
}: {
  title: string;
  children: string;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-rs-ink">{title}</h2>
      <Panel className="p-5 text-sm text-rs-ink-muted">{children}</Panel>
    </div>
  );
}
